import React, { Component } from 'react';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { MessageSourceProps, withMessages } from "react-message-source";
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import axios from 'axios';
import { addWeekPrice, findWeekPriceByYearWeekKey, getAllWeeks, searchWeekPrice } from 'services/weekPricesSettings';
import { WeekUnitCostDto } from 'ts-types/api.types';
import LoaderComponent from 'components/LoaderComponent';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Grid, Message } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import AddButton from 'components/AddButton';
import SearchResultComponent from 'components/SearchResultComponent';
import PxGrid from 'components/layout/PxGrid';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import EditActionButton from 'components/EditActionButton';
import { noop } from 'utils/functionUtils';
import UpsertWeekPriceForm from 'components/billing/week-price/UpsertWeekPriceForm';
import moment from 'moment';

interface SearchWeekQuery {
  query: string;
}

interface WeekPriceFieldProps extends FormState {
  values: Partial<SearchWeekQuery>
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  foundWeeks: Array<WeekUnitCostDto>,
  previousWeek?: WeekUnitCostDto,
  searchValues: string,
  addEditModalOpen: boolean,
  addWeekClicked: boolean,
  addedWeek?: string,
  week?: WeekUnitCostDto,
  yearWeekKey?: string,
  errorMessage?: string,
  successfullyAddedWeek?: boolean,
  weeksLoaded: boolean;
}

class WeekPricesView extends Component<Props, State> {

  cancelTokenSource = axios.CancelToken.source();
  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      foundWeeks: [],
      searchValues: "",
      addWeekClicked: false,
      addEditModalOpen: false,
      weeksLoaded: false
    };

    this.fetchRequiredData();
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {

    this.mounted = false;
    this.cancelTokenSource.cancel('Operation canceled by user');
  }

  fetchRequiredData = async () => {

    const onFinally = () => {
      this.setState({
        weeksLoaded: true
      })
    }
    const methods: Array<any> = [
      getAllWeeks(this.cancelTokenSource)
    ];

    Promise.all(methods)
        .then(response => {
          this.setState({
            foundWeeks: response[0],
          })
        })
        .finally(onFinally);
  }

  handleWeekChange = debounce(({values}: WeekPriceFieldProps) => {
    if(!this.mounted) {
      return;
    }

    this.setState({
      weeksLoaded: false
    })

    const query = values && values.query ? values.query : "";

    const onFinally = () => {
      this.setState({
        weeksLoaded: true
      })
    };

    searchWeekPrice(query, this.cancelTokenSource)
        .then(response => {
          this.setState({
            foundWeeks: response,
            searchValues: query
          });
        })
        .catch(this.handleError)
        .finally(onFinally);
  }, 300);

  openAddEditModal = (yearWeekKey?: string, addWeekClicked?: boolean) => {

    if (yearWeekKey) {
      findWeekPriceByYearWeekKey(yearWeekKey, this.cancelTokenSource)
          .then(response => {
            this.showModal(yearWeekKey, addWeekClicked, response);
          })
          .catch(noop)
    }
  };

  copyPreviousWeek = (previousWeek: WeekUnitCostDto) => {

    const nextMomentWeek = moment(previousWeek.yearWeekKey, "YYYY-WW").add(1, "week");
    const newWeek: WeekUnitCostDto = {...previousWeek, yearWeekKey: nextMomentWeek.format("YYYY-WW")};

    addWeekPrice(newWeek, this.cancelTokenSource)
        .then(this.onSaveUpsertForm)
        .catch(this.handleError)
        .finally(() => {
          this.setState({
            successfullyAddedWeek: true,
            addedWeek: newWeek.yearWeekKey,
          })
        });
  }

  showModal = (yearWeekKey?: string, addWeekClicked?: boolean, week?: WeekUnitCostDto) => {
    this.setState({
      addEditModalOpen: true,
      addWeekClicked: addWeekClicked ? addWeekClicked : false,
      week: week,
      yearWeekKey: yearWeekKey
    })
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false,
      week: undefined,
      yearWeekKey: undefined
    })
  }

  onSaveUpsertForm = () => {
    const {searchValues, addWeekClicked} = this.state;

    const onFinally = () => {
      this.setState({
        weeksLoaded: true,
        successfullyAddedWeek: addWeekClicked
      })
    };

    searchWeekPrice(searchValues, this.cancelTokenSource)
        .then(response => {
          this.setState({
            foundWeeks: response,
            addedWeek: response[0].yearWeekKey
          });
        })
        .catch(this.handleError)
        .finally(onFinally);

    this.hideModal();
  };

  handleError() {
    const {getMessage} = this.props;

    const errorMessage = getMessage('error.general');
    this.setErrorMessage(errorMessage)
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {weeksLoaded, errorMessage, successfullyAddedWeek, addedWeek} = this.state;
    const {getMessage, getMessageWithNamedParams} = this.props;

    return (
      <PraxStyledSegment>

        {successfullyAddedWeek
        && (
            <Message success onDismiss={() => this.setState({successfullyAddedWeek: false})}>
              <div className="header-message-text">
                {getMessageWithNamedParams("weekPrices.successfullyAddedWeek.message", {addedWeek: addedWeek})}
              </div>
            </Message>
        )}

        {errorMessage
        && (
            <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
              {errorMessage}
            </StyledErrorMessage>
        )}
        <div>
          {this.renderSearchWeekPricesSegment()}
        </div>

        <StyledViewTableRow>
          {!weeksLoaded
              ? <LoaderComponent message={getMessage("weekPrices.loading")}/>
              : this.renderWeekPricesSegment()}
        </StyledViewTableRow>
        {this.renderUpsertWeekPriceModel()}
      </PraxStyledSegment>
    );
  }

  renderSearchWeekPricesSegment = (): JSX.Element => {
    return (
      <FinalForm
          onSubmit={() => {}}
          initialValues={{}}
          subscription={{pristine: true}}
          render={this.renderSearchForm}
      />
    );
  }

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundWeeks} = this.state;


    const numResults = foundWeeks.length;
    let previousWeek: WeekUnitCostDto;
    let copyOrAddButtonDisabled = false;

    if (foundWeeks[0]) {
      const nextMomentWeek = moment(foundWeeks[0].yearWeekKey, "YYYY-WW").add(1, "week");
      previousWeek = {...foundWeeks[0], yearWeekKey: nextMomentWeek.format("YYYY-WW")}
      copyOrAddButtonDisabled = foundWeeks[0].yearWeekKey === moment().format("YYYY") + "-" + moment().format("WW");
    }

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="query"
                  fluid
                  component={Input}
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={16}>
              <AddButton
                  buttonMessage={getMessage("button.add")}
                  onClick={() => this.showModal("", true, previousWeek)}
                  disabled={copyOrAddButtonDisabled}
              />
              <AddButton
                  buttonMessage={getMessage("button.weekPrices.copyPrevWeek")}
                  onClick={() => this.copyPreviousWeek(foundWeeks[0])}
                  disabled={copyOrAddButtonDisabled}
              />
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleWeekChange}/>
        </PxGrid>
    );
  }

  renderWeekPricesSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundWeeks} = this.state;

    return (
      <VirtualizedTable
        rowCount={foundWeeks.length}
        rowGetter={this.weekPricesRowGetter}
        rowRenderer={this.weekPricesRowRenderer}
        rowHeight={42}
        columns={[
          {
            width: 450,
            label:(getMessage("weekPrices.week")),
            dataKey: "yearWeekKey"
          },
          {
            width: 450,
            label:(getMessage("weekPrices.laboratoryFee")),
            dataKey: "labaratoryFee",
            cellRenderer: this.priceCellFormatter
          },
          {
            width: 450,
            label:(getMessage("weekPrices.pcrPoolSupplement")),
            dataKey: "pcrPoolSupplement",
            cellRenderer: this.priceCellFormatter
          },
          {
            width: 450,
            label:(getMessage("weekPrices.poolFee")),
            dataKey: "poolFee",
            cellRenderer: this.priceCellFormatter
          },
          {
            width: 450,
            label:(getMessage("weekPrices.contractFee")),
            dataKey: "contractFee",
            cellRenderer: this.priceCellFormatter
          },
          {
            width: 100,
            label: (getMessage("actions")),
            dataKey: "yearWeekKey",
            cellRenderer: (this.actionsCellRenderer)
          }
        ]}

      />

    );
  }

  priceCellFormatter = ({cellData}: any) => {

    return Number(cellData).toFixed(2);
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <EditActionButton
            hoverMessage={getMessage('msgEdit')}
            onConfirm={() => this.openAddEditModal(cellData)}
        />
    );
  }

  weekPricesRowGetter = ({index}: any) => {
    const {foundWeeks} = this.state;

    Object.assign(foundWeeks[index], {index: index + 1});

    return foundWeeks[index];
  };

  weekPricesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
        >
          {columns}
        </div>
    );
  };

  renderUpsertWeekPriceModel = (): React.ReactNode => {
    const {addEditModalOpen, week, yearWeekKey, addWeekClicked} = this.state;

    return (
      addEditModalOpen &&
      <UpsertWeekPriceForm
        week={week}
        yearWeekKey={yearWeekKey}
        addWeekClicked={addWeekClicked}
        modalOpen={addEditModalOpen}
        onCancel={this.hideModal}
        onSave={this.onSaveUpsertForm}
      />
    );
  }

}

export default withAuthContext(withMessages(WeekPricesView));
